import React, { useState, useEffect, useRef, useContext } from 'react';
import { Container } from '@mui/material';
import { MyDataContext } from '../../ToolsComponent';
import { doPost } from '../../util/RestUtil';
import { MCP_CHAT_URL } from '../../util/RestRoutes';
import './MCPChat.css';
import ReactMarkdown from 'react-markdown';
 
interface Message {
  role: 'user' | 'assistant';
  text: string;
  chartImage?: string;
  error?: boolean;
  timestamp: number;
}

//   Component  
export default function MCPChat() {
  // Global state for sidebar width  
  const [state] = useContext(MyDataContext);
  // Local state for messages, input, loading, history, and expanded chart
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [history, setHistory]   = useState<any[]>([]);
  const [expandedChart, setExpandedChart] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
 

  // Auto-scroll  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clear chat  
  const clearChat = () => {
    if (messages.length > 0) {
      const confirmed = window.confirm('Clear chat and start fresh?');
      if (!confirmed) return;
    }
    setMessages([]);
    setHistory([]);
    setInput('');
  };

  // Send message with doPost
  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      text: input.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input.trim();
    setInput('');
    setLoading(true);

    try {
      // using doPost from RestUtil instead of axios directly, and MCP_CHAT_URL from RestRoutes
      const response = await doPost(MCP_CHAT_URL, {
        message: currentInput,
        history
      }) as any;

      const data = response.data;

      const assistantMessage: Message = {
        role: 'assistant',
        text: data.response,
        chartImage: data.chart_image,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setHistory(data.history);

    } catch (error: any) {
      const errorMessage: Message = {
        role: 'assistant',
        text: error.response?.data?.error || error.message || 'Request failed.',
        error: true,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const downloadChart = (imageData: string) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `chart-${Date.now()}.png`;
    link.click();
  };

  //  Render  
  //  Outermost element is now MUI Container with sideBarWidth calc
  return (
    <Container style={{ width: `calc(100vw - ${state.sideBarWidth}px)`, padding: '1rem' }}>

      {/* Title bar inside content area*/}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, color: '#1e3a5f' }}>MRSM AI Assistant</h2>
        {messages.length > 0 && (
          <button onClick={clearChat} className='clear-btn'>
            Clear Chat
          </button>
        )}
      </div>

      {/* Chat container*/}
      <div className='chat-container' style={{ height: 'calc(100vh - 140px)' }}>
        <div className='messages'>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
              Start a conversation by typing below...
            </div>
          )}
          {messages.map((msg, idx) => (
            <div key={idx} className={`message-row ${msg.role}`}>
              <div className={`message ${msg.error ? 'error' : ''}`}>
                <div className='text'>
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
                {msg.chartImage && (
                  <div className='chart'>
                    <img
                      src={`data:image/png;base64,${msg.chartImage}`}
                      alt='Chart'
                      onClick={() => setExpandedChart(msg.chartImage!)}
                    />
                    <button onClick={() => downloadChart(msg.chartImage!)}
                            className='download-btn'>
                      Download Chart
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className='message-row assistant'>
              <div className='message'>
                <div className='typing'>
                  <span/><span/><span/>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className='input-container'>
          <input ref={inputRef} type='text' value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder='Ask about the data or request visualizations...'
            disabled={loading} className='input' />
          <button onClick={sendMessage}
            disabled={loading || !input.trim()} className='send-btn'>
            {loading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>

      {/* Chart modal*/}
      {expandedChart && (
        <div className='modal' onClick={() => setExpandedChart(null)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setExpandedChart(null)} className='close'>x</button>
            <img src={`data:image/png;base64,${expandedChart}`} alt='Chart' />
            <button onClick={() => downloadChart(expandedChart)} className='download'>
              Download Chart
            </button>
          </div>
        </div>
      )}
    </Container>
  );
}
